import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {ISmartDogTrainersByThingNameSdkRequest} from 'rsc-iot-cloud-lib-sdk/petsafe/products/smart-dog-trainers/get-by-thing-names';
import {ISmartFeedsByThingNamesRequest} from 'rsc-iot-cloud-lib-sdk/petsafe/products/smart-feed/get-by-thing-names';
import {ISmartDoorsByThingNamesRequest} from 'rsc-iot-cloud-lib-sdk/petsafe/products/smart-doors/get-by-thing-names';
import {IScoopfreesByThingNameSdkRequest} from 'rsc-iot-cloud-lib-sdk/petsafe/products/scoopfrees/get-by-thing-names';
import {IWirelessFencesByThingNamesRequest} from 'rsc-iot-cloud-lib-sdk/petsafe/products/wireless-fence/get-by-thing-names';
import {environment} from '@env/environment';
import {IScoopfreeActivityByThingNameSdkRequest} from 'rsc-iot-cloud-lib-sdk/petsafe/products/scoopfrees/get-activity-by-thing-name';
import {ISmartDoorActivityByThingNameSdkRequest} from 'rsc-iot-cloud-lib-sdk/petsafe/products/smart-doors/get-activity-by-thing-name';
import {IProductPreferencesByThingNameRequestBody} from 'rsc-iot-cloud-lib-sdk/petsafe/products/preferences/product';
import {IPetProductPreferencesByThingNameRequestBody} from 'rsc-iot-cloud-lib-sdk/petsafe/products/preferences/pet/product';
import {IUserProductPreferencesByThingNameRequestBody} from 'rsc-iot-cloud-lib-sdk/petsafe/products/preferences/user/product';
import {IUserPreferencesByThingNameRequestBody} from 'rsc-iot-cloud-lib-sdk/petsafe/products/preferences/user';
import {IUserMemberPreferencesByThingNameRequestBody} from 'rsc-iot-cloud-lib-sdk/petsafe/products/preferences/user/member';
import {IUserMobileDevicePreferencesByThingNameRequestBody} from 'rsc-iot-cloud-lib-sdk/petsafe/products/preferences/user/mobile-device';
import {
  IFirmwareHistoryByProductRequestBody,
  IFirmwareHistoryByProductResponseBody
} from 'rsc-iot-cloud-lib-sdk/petsafe/products/firmware/history-by-product';
import {FirmwareHistoryByProduct} from '@model/product/firmware/history-by-product';
import {LoginService} from '@util/login.service';

/**
 * Angular HTTP Client request options including headers
 */
const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'x-api-key': environment.ccApiKey
  })
};

@Injectable({
  providedIn: 'root'
})
export class CustomerCareService {

  /**
   * instance of directory service endpoint url.
   * Depending on the selected environment it
   * will point at either develop, staging or production
   */
  private url = environment.customerCareAPI;

  constructor(
    private http: HttpClient,
    private loginService: LoginService
  ) { }


  public getAccount(id: string): Observable<any> {
    this.updateToken();
    return this.http.get<any>(`${this.url}/account/${id}`, httpOptions)
      .pipe(map(data => data));
  }

  public getAccountByEmail(email: string): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/users/get-by-emails`, { emails: [email] }, httpOptions)
      .pipe(map(data => data));
  }

  public getGroup(id: string): Observable<any> {
    this.updateToken();
    return this.http.get<any>(`${this.url}/group/${id}`, httpOptions)
      .pipe(map(data => data));
  }

  public getPetProductsByThingNames(thingNames: Array<string>): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/pet-products/by-thing-names`, { thingNames }, httpOptions)
      .pipe(map(data => data));
  }

  public getPetTagsByPetIds(petIds: Array<string>): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/pet-tags/by-pet-ids`, { petIds }, httpOptions)
      .pipe(map(data => data));
  }

  public getPetTagsByTagIds(tagIds: Array<string>): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/pet-tags/by-pet-ids`, { tagIds }, httpOptions)
      .pipe(map(data => data));
  }

  public getPetsByPetIds(petIds: Array<string>): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/pets/by-pet-ids`, { petIds }, httpOptions)
      .pipe(map(data => data));
  }

  public getProductPreferences(requestBody: IProductPreferencesByThingNameRequestBody): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/product-preferences`, requestBody, httpOptions)
      .pipe(map(data => data));
  }

  public getPetProductPreferences(requestBody: IPetProductPreferencesByThingNameRequestBody): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/pet-product-preferences`, requestBody, httpOptions)
      .pipe(map(data => data));
  }

  public getUserPreferences(requestBody: IUserPreferencesByThingNameRequestBody): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/user-preferences`, requestBody, httpOptions)
      .pipe(map(data => data));
  }

  public getUserMemberPreferences(requestBody: IUserMemberPreferencesByThingNameRequestBody): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/user-member-preferences`, requestBody, httpOptions)
      .pipe(map(data => data));
  }

  public getUserMobileDevicePreferences(requestBody: IUserMobileDevicePreferencesByThingNameRequestBody): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/user-mobile-device-preferences`, requestBody, httpOptions)
      .pipe(map(data => data));
  }

  public getUserProductPreferences(requestBody: IUserProductPreferencesByThingNameRequestBody): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/user-product-preferences`, requestBody, httpOptions)
      .pipe(map(data => data));
  }

  public getScoopfreesByThingNames(requestBody: IScoopfreesByThingNameSdkRequest): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/scoopfrees/get-by-thing-names`, requestBody, httpOptions)
      .pipe(map(data => data));
  }

  public getScoopfreeActivity(requestBody: IScoopfreeActivityByThingNameSdkRequest): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/scoopfrees/get-activity-by-thing-names`, requestBody, httpOptions)
      .pipe(map(data => data));
  }

  public getSmartDogTrainersByThingNames(requestBody: ISmartDogTrainersByThingNameSdkRequest): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/smart-dog-trainers/get-by-thing-names`, requestBody, httpOptions)
      .pipe(map(data => data));
  }

  public getSmartFeedsByThingNames(requestBody: ISmartFeedsByThingNamesRequest): Observable<any> {
    this.updateToken();
    // return of({
    //   data: [
    //     {
    //       thingName: 'ThingName',
    //       batteryVoltage: 'Battery Voltage',
    //       connectionStatus: 0,
    //       hopperStatus: 0, // is_food_low
    //       adapterStatus: 0, // is_adapter_installed
    //       batteriesStatus: 0, // is_batteries_installed
    //       firmwareVersion: '0.0.0',
    //       productVersion: 1,
    //       revisionDesired: 1,
    //       revisionReported: 1,
    //       foodSensorCurrent: 1,
    //       foodSensorReference: 1,
    //       networkRssi: 1,
    //       networkSnr: 1,
    //       connectionStatusTimestamp: new Date().toISOString(),
    //       createdAt: new Date().toISOString(),
    //       updatedAt: new Date().toISOString(),
    //       deletedAt: new Date().toISOString(),
    //       updatedBy: 1,
    //       createdBy: 1,
    //       inventory: 1,
    //     }
    //   ]
    // });
    return this.http.post<any>(`${this.url}/smart-feeds/get-by-thing-names`, requestBody, httpOptions)
      .pipe(map(data => data));
  }

  public getSmartDoorsByThingNames(requestBody: ISmartDoorsByThingNamesRequest): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/smart-doors/get-by-thing-names`, requestBody, httpOptions)
      .pipe(map(data => data));
  }
  public getSmartDoorActivity(requestBody: ISmartDoorActivityByThingNameSdkRequest): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/smart-doors/get-activity-by-thing-names`, requestBody, httpOptions)
      .pipe(map(data => data));
  }


  public getWirelessFencesByThingNames(requestBody: IWirelessFencesByThingNamesRequest): Observable<any> {
    this.updateToken();
    return this.http.post<any>(`${this.url}/wireless-fences/get-by-thing-names`, requestBody, httpOptions)
      .pipe(map(data => data));
  }

  public getFirmwareHistoryByProduct(requestBody: IFirmwareHistoryByProductRequestBody): Observable<IFirmwareHistoryByProductResponseBody> {
    this.updateToken();
    return this.http.post<{data: IFirmwareHistoryByProductResponseBody}>(`${this.url}/firmware/history-by-product`, requestBody, httpOptions)
      .pipe(map(response => FirmwareHistoryByProduct.sort(response.data)));
  }

  /**
   * updateToken sets the most upto date Auth Token in the header
   *
   */
  private updateToken(): void {
    httpOptions.headers = httpOptions.headers.set('Authorization', this.loginService.getToken());
  }
}
